import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { FunnelVisualization } from '../../components/dashboard/FunnelVisualization';
import { ChannelGrid } from '../../components/dashboard/ChannelTile';
import { TrendChart, PieChartComponent, BarChartComponent } from '../../components/dashboard/Charts';
import { reportsApi, dashboardApi } from '../../lib/api';
import { toast } from 'sonner';
import { 
    Target, 
    Users, 
    Send, 
    MessageSquare, 
    ThumbsUp, 
    ThumbsDown,
    Clock,
    TrendingUp,
    Award,
    AlertCircle
} from 'lucide-react';

export default function CustomerDashboard() {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [reports, setReports] = useState([]);
    const [hasData, setHasData] = useState(false);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            const [statsRes, reportsRes] = await Promise.all([
                dashboardApi.getStats(),
                reportsApi.getAll(),
            ]);

            setStats(statsRes.data);
            setReports(reportsRes.data);
            
            // Check if there's actual data
            const hasMetrics = statsRes.data?.metrics && Object.values(statsRes.data.metrics).some(v => v > 0);
            const hasReports = reportsRes.data && reportsRes.data.length > 0;
            setHasData(hasMetrics || hasReports);
        } catch (error) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    // Use overall metrics from stats (manually set by admin) or from reports
    const metrics = stats?.metrics || {};
    const funnel = stats?.funnel || {};
    
    // Get latest report for channel data only
    const latestReport = reports.length > 0 ? reports[0] : null;
    const linkedin = latestReport?.linkedin || {};
    const email = latestReport?.email || {};
    const ads = latestReport?.ads || {};
    const inbound = latestReport?.inbound || {};

    // Prepare trend data from reports
    const trendData = reports.slice(0, 8).reverse().map((report, index) => ({
        name: `Week ${index + 1}`,
        mqls: report.metrics?.mqls || 0,
        leads: report.metrics?.qualified_leads || 0,
        responses: report.metrics?.responses || 0,
    }));

    // Channel distribution data - only show if there's actual data
    const channelDistribution = [
        { name: 'LinkedIn', value: linkedin.mqls || 0 },
        { name: 'Email', value: email.mqls || 0 },
        { name: 'Ads', value: Math.round((ads.clicks || 0) / 10) || 0 },
        { name: 'Inbound', value: inbound.total_leads || 0 },
    ];

    // Industry breakdown
    const industryData = Object.entries(linkedin.industry_breakdown || {}).map(([name, value]) => ({
        name,
        responses: value,
    }));

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-content-secondary">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // Show empty state if no data
    if (!hasData) {
        return (
            <div className="min-h-screen" data-testid="customer-dashboard">
                <Header 
                    title="Dashboard" 
                    subtitle="360° view of your ABM performance"
                />
                <div className="p-8">
                    <div className="card-base p-12 text-center">
                        <AlertCircle className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-xl font-heading font-bold text-white mb-2">
                            No Data Available Yet
                        </h3>
                        <p className="text-content-secondary max-w-md mx-auto">
                            Your dashboard will populate once your admin team updates the metrics and reports. 
                            Please check back later or contact your account manager.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="customer-dashboard">
            <Header 
                title="Dashboard" 
                subtitle="360° view of your ABM performance"
            />

            <div className="p-8 space-y-8">
                {/* KPI Cards */}
                <section>
                    <h2 className="text-sm font-bold uppercase tracking-wider text-content-muted mb-4">
                        KEY METRICS
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-4">
                        <KPICard 
                            title="Target Accounts" 
                            value={metrics.target_accounts || 0} 
                            icon={Target}
                            color="red"
                            tooltip="Total accounts in your ABM campaign"
                            delay={0}
                        />
                        <KPICard 
                            title="Total Contacts" 
                            value={metrics.total_contacts || 0}
                            icon={Users}
                            tooltip="Contacts across all target accounts"
                            delay={50}
                        />
                        <KPICard 
                            title="Total Reachouts" 
                            value={metrics.total_reachouts || 0}
                            icon={Send}
                            tooltip="Total outbound touches"
                            delay={100}
                        />
                        <KPICard 
                            title="Connected" 
                            value={metrics.connected || 0}
                            icon={MessageSquare}
                            tooltip="Total connections made"
                            delay={150}
                        />
                        <KPICard 
                            title="Positive" 
                            value={metrics.positive_responses || 0}
                            icon={ThumbsUp}
                            color="inbound"
                            tooltip="Positive engagement signals"
                            delay={200}
                        />
                        <KPICard 
                            title="Rejects" 
                            value={metrics.rejects || 0}
                            icon={ThumbsDown}
                            tooltip="Declined or uninterested"
                            delay={250}
                        />
                        <KPICard 
                            title="Future Leads" 
                            value={metrics.future_leads || 0}
                            icon={Clock}
                            color="ads"
                            tooltip="Nurture for future opportunities"
                            delay={300}
                        />
                        <KPICard 
                            title="MQLs" 
                            value={metrics.mqls || 0}
                            icon={TrendingUp}
                            color="red"
                            tooltip="Marketing Qualified Leads"
                            delay={350}
                        />
                        <KPICard 
                            title="Qualified Leads" 
                            value={metrics.qualified_leads || 0}
                            icon={Award}
                            color="inbound"
                            tooltip="Sales-ready qualified leads"
                            delay={400}
                        />
                    </div>
                </section>

                {/* Funnel and Channel Performance */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <FunnelVisualization 
                        data={funnel} 
                        className="lg:col-span-1"
                    />
                    <div className="lg:col-span-2">
                        <h2 className="text-sm font-bold uppercase tracking-wider text-content-muted mb-4">
                            CHANNEL PERFORMANCE
                        </h2>
                        <ChannelGrid 
                            linkedinData={linkedin}
                            emailData={email}
                            adsData={ads}
                            inboundData={inbound}
                        />
                    </div>
                </section>

                {/* Charts Row - only show if there's data */}
                {(trendData.length > 0 || channelDistribution.some(c => c.value > 0) || industryData.length > 0) && (
                    <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {trendData.length > 0 && (
                            <TrendChart 
                                data={trendData}
                                dataKey="mqls"
                                title="MQL TREND"
                                color="#FF0000"
                                className="lg:col-span-1"
                            />
                        )}
                        {channelDistribution.some(c => c.value > 0) && (
                            <PieChartComponent 
                                data={channelDistribution.filter(c => c.value > 0)}
                                title="CHANNEL CONTRIBUTION"
                                className="lg:col-span-1"
                            />
                        )}
                        {industryData.length > 0 && (
                            <BarChartComponent 
                                data={industryData}
                                dataKeys={['responses']}
                                title="INDUSTRY BREAKDOWN"
                                className="lg:col-span-1"
                            />
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
